import React, {useState} from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import {Close} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import {formStyles} from '../../util/styles';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import { createCase } from '../../db/actions';
import {FormControl, FormLabel, Paper} from '@mui/material';
import { useAuth } from '../../util/authContext';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import GridStartIcon from '../../components/GridStartIcon';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import StandardFlowIcon from "../../components/StandardFlowIcon";
import SnakeFlowIcon from "../../components/SnakeFlowIcon";
import MenuItem from "@mui/material/MenuItem";

const CreateModal = ({open, onClose, onCreate}) => {
  const classes = formStyles();
  const { data: userData } = useAuth();
  
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [comments, setComments] = useState('');
  const [jurySize, setJurySize] = useState(18);
  const [numberOfAlternates, setNumberOfAlternates] = useState(0);
  const [alternateProsecutionStrikes, setAlternateProsecutionStrikes] = useState(0);
  const [alternateDefendantStrikes, setAlternateDefendantStrikes] = useState(0);
  const [jurorsPerRow, setJurorsPerRow] = useState(6);
  const [seatNumberStartPosition, setSeatNumberStartPosition] = useState("topLeft");
  const [seatNumberFlow, setSeatNumberFlow] = useState("standard");
  const [prosecutionStrikes, setProsecutionStrikes] = useState(10);
  const [defendantStrikes, setDefendantStrikes] = useState(10);
  const [civilCase, setCivilCase] = useState(false);
  const [demographicCategories, setDemographicCategories] = useState([]);
  const [showDemographics, setShowDemographics] = useState(false);
  const [errors, setErrors] = useState([]);
  
  const handleClose = () => {
    onClose();
  };
  
  const handleAddDemographicCategory = (chip) => {
    let newDemos = [...demographicCategories];
    chip = chip.trim();
    if (newDemos.indexOf(chip) === -1) {
      newDemos.push(chip);
      
      setDemographicCategories(newDemos);
    }
  };
  
  const handleRemoveDemographicCategory = (chip, index) => {
    let newDemos = [...demographicCategories];
    newDemos.splice(index, 1);
    setDemographicCategories(newDemos);
  };
  
  const handleCreate = async () => {
    let errorList = [];
    
    if (!name) {
      errorList.push('Name must be populated');
    }
    
    if (!number) {
      errorList.push('Number must be populated');
    }
    
    if (!jurySize) {
      errorList.push('Number of juror seats must be populated');
    }
    
    if (jurySize > 60) {
      errorList.push("Number of juror seats can't be greater than 60");
    }
    
    if (!prosecutionStrikes) {
      let typeTitle = civilCase ? 'Plaintiff' : 'Prosecution';
      errorList.push(typeTitle + ' strikes must be populated');
    }
    
    if (!defendantStrikes) {
      errorList.push('Defendant strikes must be populated');
    }
    
    if (errorList.length > 0) {
      setErrors(errorList);
    } else {
      const createCaseBody = {
        name: name,
        number: number,
        details: comments,
        juryBoxSize: jurySize,
        prosecutionPeremptStrikes: prosecutionStrikes,
        defendantPeremptStrikes: defendantStrikes,
        jurorsPerRow: jurorsPerRow,
        seatNumberStartPosition: seatNumberStartPosition,
        seatNumberFlow: seatNumberFlow,
        civilCase: civilCase,
        createdAt: (new Date()).toISOString(),
        demographicCategories: demographicCategories,
        showDemographics: showDemographics,
        numberOfAlternates,
        alternateProsecutionPeremptStrikes: alternateProsecutionStrikes,
        alternateDefendantPeremptStrikes: alternateDefendantStrikes,
        notes: "",
        notesStyles: [],
        revision: 0
      };
      const newCase = await createCase(createCaseBody, userData?.email);
      onCreate(newCase._id);
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle sx={{ pr: 6 }}>
        Create Case
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
        >
          <Close/>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Collapse in={errors && errors.length > 0}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Create Failed:
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Alert>
        </Collapse>
        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              required
              size="small"
              fullWidth
              id="number"
              name="number"
              label="Case Number"
              autoFocus
              value={number}
              onChange={(event) => setNumber(event.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              required
              size="small"
              fullWidth
              id="name"
              name="name"
              label="Case Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              size="small"
              fullWidth
              id="comments"
              name="comments"
              label="Description"
              multiline
              rows={2}
              value={comments}
              onChange={(event) => setComments(event.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <FormControl>
              <FormLabel>Type</FormLabel>
              <RadioGroup row value={civilCase ? "1" : "0"}
                          onChange={(e) => setCivilCase(e.target.value === "1")}>
                <FormControlLabel control={<Radio size="small" />} value={"0"} label="Criminal" />
                <FormControlLabel control={<Radio size="small" />} value={"1"} label="Civil" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid size={6}>
            <TextField
              required
              size="small"
              fullWidth
              id="prosecution-strikes"
              name="prosecution-strikes"
              label={`${civilCase ? 'Plaintiff' : 'Prosecution'} Peremptory Strikes`}
              type="number"
              value={prosecutionStrikes}
              onChange={(event) => setProsecutionStrikes(event.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              required
              size="small"
              fullWidth
              id="defendant-strikes"
              name="defendant-strikes"
              label="Defense Peremptory Strikes"
              type="number"
              value={defendantStrikes}
              onChange={(event) => setDefendantStrikes(event.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline" display="block" gutterBottom>Alternates</Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TextField
                    size="small"
                    fullWidth
                    id="number-of-alternates"
                    name="number-of-alternates"
                    label="Number of alternates"
                    type="number"
                    value={numberOfAlternates}
                    onChange={(event) => setNumberOfAlternates(event.target.value)}
                    slotProps={{ htmlInput: { min: 0, max: 30 } }}
                  />
                </Grid>
                {numberOfAlternates > 0 && (
                  <>
                    <Grid size={6}>
                      <TextField
                        required
                        size="small"
                        fullWidth
                        id="alternate-prosecution-strikes"
                        name="alternate-prosecution-strikes"
                        label={`${civilCase ? 'Plaintiff' : 'Prosecution'} Peremptory Strikes`}
                        type="number"
                        value={alternateProsecutionStrikes}
                        onChange={(event) => setAlternateProsecutionStrikes(event.target.value)}
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        required
                        size="small"
                        fullWidth
                        id="alternate-defendant-strikes"
                        name="alternate-defendant-strikes"
                        label="Defense Peremptory Strikes"
                        type="number"
                        value={alternateDefendantStrikes}
                        onChange={(event) => setAlternateDefendantStrikes(event.target.value)}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          </Grid>
          <Grid size={12}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={demographicCategories || []}
              onChange={(event, newValue) => {
                setDemographicCategories(newValue.map(v => v.trim()).filter(Boolean));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="Demographics Tracking Categories" />
              )}
            />
            <Typography variant="caption" color="text.secondary">
              Press <code>Enter</code> to create a category
            </Typography>
          </Grid>
          <Grid size={12}>
            <FormControl>
              <FormLabel>Show Demographics on Juror Cards</FormLabel>
              <RadioGroup row value={showDemographics ? "1" : "0"}
                          onChange={(e) => setShowDemographics(e.target.value === "1")}>
                <FormControlLabel control={<Radio size="small" />} value={"0"} label="No" />
                <FormControlLabel control={<Radio size="small" />} value={"1"} label="Yes" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              required
              size="small"
              fullWidth
              id="jury-size"
              name="jury-size"
              label="Number of juror seats (including alternates or six pack)"
              type="number"
              value={jurySize}
              onChange={(event) => setJurySize(event.target.value)}
              slotProps={{ htmlInput: { min: 0, max: 100 } }}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              select
              size="small"
              fullWidth
              label="Max Jurors Per Row"
              value={parseInt(jurorsPerRow)}
              onChange={(e) => setJurorsPerRow(parseInt(e.target.value))}
            >
              {Array.from({ length: 17 }, (_, i) => i + 4).map(n => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={12}>
            <FormControl>
              <FormLabel>Seat Number Starting Position</FormLabel>
              <RadioGroup row value={seatNumberStartPosition}
                          onChange={(e) => setSeatNumberStartPosition(e.target.value)}>
                <FormControlLabel control={<Radio size="small" />}
                                  value="topLeft"
                                  label={<GridStartIcon className={classes.gridStartIcon} />}
                                  title="Top Left" />
                <FormControlLabel control={<Radio size="small" />}
                                  value="topRight"
                                  label={<GridStartIcon className={classes.gridStartIcon}
                                                        style={{transform: "rotate(90deg)"}} />}
                                  title="Top Right" />
                <FormControlLabel control={<Radio size="small" />}
                                  value="bottomLeft"
                                  label={<GridStartIcon className={classes.gridStartIcon}
                                                        style={{transform: "rotate(270deg)"}} />}
                                  title="Bottom Left" />
                <FormControlLabel control={<Radio size="small" />}
                                  value="bottomRight"
                                  label={<GridStartIcon className={classes.gridStartIcon}
                                                        style={{transform: "rotate(180deg)"}} />}
                                  title="Bottom Right" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <FormControl>
              <FormLabel>Seat Number Flow</FormLabel>
              <RadioGroup row value={seatNumberFlow}
                          onChange={(e) => setSeatNumberFlow(e.target.value)}>
                <FormControlLabel
                  control={<Radio size="small" />}
                  value="standard"
                  label={
                    <div className={classes.flowLabel}>
                      <StandardFlowIcon className={classes.gridStartIcon} />
                      <span className={classes.flowLabelText}>Standard</span>
                    </div>
                  }
                  title="Standard" />
                <FormControlLabel
                  control={<Radio size="small" />}
                  value="snake"
                  label={
                    <div className={classes.flowLabel}>
                      <SnakeFlowIcon className={classes.gridStartIcon} />
                      <span className={classes.flowLabelText}>Snaking</span>
                    </div>
                  }
                  title="Snaking" />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={handleCreate}
          color="primary"
          variant="contained"
        >
          Create
        </Button>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateModal;
